import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/Button";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/contexts/I18nContext";
import { COMPOSER_URL } from "@/lib/links";
import { isWelcomeDismissed, setWelcomeDismissed } from "./dismissal";

/**
 * A teacher's first look at Backstage.
 *
 * Three sentences about what is here for them, and one about where the other
 * app is. A teacher was given credentials by their owner and a link to this
 * place; nothing told them that the scenes they will teach are written
 * somewhere else, with the same sign-in. This panel is that sentence.
 */
const HERE: readonly { to: string; labelKey: string; bodyKey: string }[] = [
  { to: "/groups", labelKey: "onboarding.teacher.groups", bodyKey: "onboarding.teacher.groupsBody" },
  { to: "/sessions", labelKey: "onboarding.teacher.lessons", bodyKey: "onboarding.teacher.lessonsBody" },
  { to: "/kits", labelKey: "onboarding.teacher.kits", bodyKey: "onboarding.teacher.kitsBody" },
];

export function TeacherWelcome() {
  const t = useT();
  const { user } = useAuth();
  const uid = user?.uid ?? "";
  const [dismissed, setDismissed] = useState(() => isWelcomeDismissed(uid));

  if (dismissed) return null;

  return (
    <section aria-labelledby="teacher-welcome" className="card mb-8 p-6">
      <h2 id="teacher-welcome" className="section-title">
        {t("onboarding.teacher.title")}
      </h2>
      <p className="mt-1 text-sm text-ink-muted">{t("onboarding.teacher.intro")}</p>

      <ul className="mt-5 flex flex-col gap-3">
        {HERE.map((item) => (
          <li key={item.to} className="text-sm">
            <Link to={item.to} className="font-medium text-ink hover:underline">
              {t(item.labelKey)}
            </Link>
            <p className="mt-0.5 text-ink-muted">{t(item.bodyKey)}</p>
          </li>
        ))}
      </ul>

      <div className="surface-inset mt-6 px-4 py-3">
        <a href={COMPOSER_URL} className="text-sm font-medium text-ink hover:underline">
          {t("nav.openComposer")}
          <span aria-hidden="true" className="ml-1 text-ink-faint">↗</span>
        </a>
        <p className="mt-0.5 text-sm text-ink-muted">{t("onboarding.teacher.composerBody")}</p>
      </div>

      <div className="mt-5">
        <Button
          variant="secondary"
          onClick={() => {
            setWelcomeDismissed(uid, true);
            setDismissed(true);
          }}
        >
          {t("onboarding.teacher.dismiss")}
        </Button>
      </div>
    </section>
  );
}
